import isEmpty from "lodash/isEmpty";
import Choices from "./choices";
import Search from "./search";

import isEqual from "lodash/isEqual";

/**
 * For all input components that support resource reference, as `VaSelectInput`, `VaRadioGroupInput` or `VaAutocompleteInput`.
 */
export default {
  mixins: [Choices, Search],
  props: {
    /**
     * Value to be selected, array if multiple.
     * @model
     */
    value: {
      type: [String, Number, Array],
      default: null,
    },
    /**
     * Name of resource to search into.
     */
    reference: String,
  },
  data() {
    return {
      loading: false,
      items: null,
      totalItems: 0,
    };
  },
  watch: {
    filter: {
      async handler(val, oldVal) {
        if (val && !isEqual(val, oldVal)) {
          this.clearItemsList();
          await this.updateItemsList();
        }
      },
    },
  },
  computed: {
    getItemText() {
      if (this.itemText !== "text") {
        return this.itemText;
      }
      if (this.reference) {
        let resource = this.$admin.getResource(this.reference);
        return resource.label || "label";
      }
      return this.itemText;
    },
    getItemValue() {
      if (this.itemValue !== "value") {
        return this.itemValue;
      }
      if (this.reference) {
        return "id";
      }
      return this.itemValue;
    },
    getFields() {
      if (!isEmpty(this.fields)) {
        return this.fields;
      }

      let resource = this.$admin.getResource(this.reference);
      return (
        resource.autocompleteFields ||
        (typeof this.getItemText === "string"
          ? [this.getItemValue, this.getItemText]
          : [])
      );
    },
  },
  methods: {
    getItem(value) {
      if (!value) {
        return value;
      }
      let input = this.multiple
        ? value.map((v) => v[this.getItemValue] || v)
        : value[this.getItemValue] || value;

      return input;
    },
    async fetchChoices(search = null, page = 1) {
      if (!this.reference || this.loading) {
        return;
      }

      if (this.disabled && this.emptyDisabled) {
        this.clearItemsList();
        return;
      }

      this.loading = true;

      /**
       * Load paginated and sorted data list
       */
      let response = await this.$store.dispatch(`${this.reference}/getList`, {
        fields: {
          [this.reference]: this.getFields,
        },
        include: this.include,
        disablePagination: this.disablePagination,
        pagination: {
          page: page,
          perPage: this.itemsPerPage,
        },
        sort: this.sortBy.map((by, index) => {
          return { by, desc: this.sortDesc[index] };
        }),
        filter: {
          ...this.filter,
          ...(this.searchQuery && search && { [this.searchQuery]: search }),
        },
      });
      /**
       * https://vuetifyjs.com/en/api/v-autocomplete/#props
       * Apparently, if an item has a property named 'header',
       * it becomes unselectable. That's why we have to remove
       * property 'header' from all the items
       */
      Array.isArray(response.data) &&
        (response.data = response.data.map((item) => {
          delete item.header;
          return item;
        }));
      this.loading = false;
      this.totalItems = response.total;
      return response.data;
    },
    async fetchCurrentChoices(ids) {
      if (!this.reference || isEmpty(ids)) {
        return;
      }

      this.loading = true;

      /**
       * Fetch related item records
       * Used for preloaded autocomplete inputs
       */
      let { data } = await this.$store.dispatch(`${this.reference}/getMany`, {
        fields: {
          [this.reference]: this.getFields,
        },
        include: this.include,
        ids,
      });

      this.loading = false;
      return data;
    },
    /**
     * Expose a method to update internal options list.
     * This method can be accessed by assigning a ref to the select/autocomplete component,
     * and calling this method directly;
     *
     * This is used to avoid assigning a :key to the select/autocomplete input and incrementing it
     * to re-render the select/autocomplete component and re-fetch items.
     *
     * Use-case example: page domain changes and we need to fetch only domain-specific page categories.
     * Without this method, we'd have to assign a :key prop to the select and increment it to trigger a re-render,
     * and in turn trigger the re-fetch of the items. Not a clean solution.
     */
    async updateItemsList() {
      this.items = await this.fetchChoices();
      this.itemsCopy = this.items;
    },
    /**
     * Clears select/autocomplete options (choices).
     *
     * Mostly useful for autocomplete type inputs, because if we call updateItemsList() with an autocomplete input,
     * it'd fetch all the possible autocomplete options, but the whole idea of autocomplete input is to fetch for what we search for
     * in the search bar, delaying the request.
     *
     * Use-case example: after changing the domain, clear all the options from an autocomplete
     * (because some of the options might have been fetched using the old domain, thus the options were outdated),
     * and leave autocomplete options empty until the user starts searching (regular autocomplete functionality)
     *
     * This could be achieved using - this.$refs.[refName].items = [], but having a method is more declarative :).
     */
    clearItemsList() {
      this.items = [];
      this.itemsCopy = this.items;
    },
  },
};
